import React from 'react';
import intl from 'react-intl-universal';

import { PortalInstance } from '@/components/PortalConfirm';
import BaseService from '@/api/checkInBase';
import { BizErrorCode } from '@/api/bizCode';
import { BillStatus, MainOrderStatus } from '@/config/constant';
import { CheckInGuestInfo } from '@/redux/reducers/reservation/base.d';

import styles from './index.module.scss';

/**
 * 离开页面二次确认
 * @param portal
 * @returns 是否离开
 */
export const showLeaveConfirm: (portal: PortalInstance) => Promise<boolean> = (portal) => {
  return new Promise((resolve) => {
    portal.showDrawer({
      title: intl.get('reservation_cmp_modal_leave_confirm_title'),
      content: intl.get('reservation_cmp_modal_leave_confirm_content'),
      cancelText: intl.get('reservation_cmp_modal_leave_confirm_cancel_btn'),
      confirmText: intl.get('reservation_cmp_modal_leave_confirm_confirm_btn'),
      onConfirm: () => {
        portal.closeDrawer();
        resolve(false);
      },
      onCancel: () => {
        portal.closeDrawer();
        resolve(true);
      },
    });
  });
};

// // 脏房提醒
export const showDirtyConfirm: (
  portal: PortalInstance,
  dirtyList: { roomTypeName: string; roomNo: string; roomId: number }[],
) => Promise<boolean> = (portal, dirtyList) => {
  return new Promise((resolve) => {
    if (dirtyList && dirtyList.length) {
      portal.showDrawer({
        title: intl.get('reservation_cmp_modal_dirty_room_title'),
        content: (
          <div className={styles.dirtyHintWrapper}>
            <div>{intl.get('reservation_cmp_modal_dirty_room_content')}</div>
            <ul>
              {dirtyList.map((item) => {
                return (
                  <li className={styles.roomItem} key={item.roomId}>
                    <div className={styles.itemType}>{item.roomTypeName}</div>
                    <div className={styles.itemNo}>{item.roomNo}</div>
                  </li>
                );
              })}
            </ul>
          </div>
        ),
        confirmText: intl.get('app_confirm_verify_btn'),
        onConfirm: () => {
          portal.closeDrawer();
          resolve(true);
        },
        // cancelText: '关闭',
        onCancel: () => {
          portal.closeDrawer();
          resolve(false);
        },
      });
    } else {
      resolve(false);
    }
  });
};

// 同期预订提醒
export const showSameOrderConfirm: (portal: PortalInstance) => Promise<boolean> = (portal) => {
  return new Promise((resolve) => {
    portal.showDrawer({
      title: intl.get('reservation_cmp_modal_same_order_title'),
      content: intl.get('reservation_cmp_modal_same_order_content'),
      cancelText: intl.get('reservation_cmp_modal_same_order_cancel_btn'),
      confirmText: intl.get('reservation_cmp_modal_same_order_confirm_btn'),
      closable: false,
      className: styles.sameOrderModal,
      onConfirm: () => {
        portal.closeDrawer();
        resolve(true);
      },
      onCancel: () => {
        portal.closeDrawer();
        resolve(false);
      },
    });
  });
};

interface ErrRes {
  code: number;
  msg: string;
}

// 数据过期提醒
const OutdateActionTip = {
  reserve: intl.get('reservation_cmp_modal_outdate_content_reserve'),
  exchange: intl.get('reservation_cmp_modal_outdate_content_exchange'),
  checkIn: intl.get('reservation_cmp_modal_outdate_content_checkin'),
};
export const showOutdateConfirm: (
  portal: PortalInstance,
  err: ErrRes,
  type: keyof typeof OutdateActionTip,
) => Promise<boolean> = (portal, err, type) => {
  const refreshCodeList = [12006, 12017];
  return new Promise((resolve) => {
    if (err && refreshCodeList.includes(err.code)) {
      // 错误码匹配
      portal.showDrawer({
        title: intl.get('reservation_cmp_modal_outdate_title'),
        closable: false,
        content: OutdateActionTip[type],
        confirmText: intl.get('reservation_cmp_modal_outdate_confirm_btn'),
        onConfirm: () => {
          portal.closeDrawer();
          resolve(true);
        },
      });
    } else {
      resolve(false);
    }
  });
};

// 查房提醒
const WardRoundTip = {
  checkIn: intl.get('reservation_cmp_modal_ward_round_content_checkin'),
  checkOut: intl.get('reservation_cmp_modal_ward_round_content_checkout'),
  checkInExchange: intl.get('reservation_cmp_modal_ward_round_content_exchange'),
};
export const showWardRoundConfirm: (
  portal: PortalInstance,
  err: ErrRes,
  type: keyof typeof WardRoundTip,
) => Promise<void> = (portal, err, type) => {
  return new Promise((resolve) => {
    if (err && err.code === BizErrorCode.NEED_ROOM_CHECK) {
      const errMsg = err.msg || '';
      portal.showDrawer({
        title: intl.get('reservation_cmp_modal_ward_round_title'),
        content: (
          <div>
            <div>{WardRoundTip[type]}</div>
            <div className={styles.roomText}>{errMsg.split('：')[1] || errMsg}</div>
          </div>
        ),
        confirmText: intl.get('app_confirm_close_btn'),
        className: styles.checkOutConfirmModal,
        onCancel: () => {
          portal.closeDrawer();
          resolve();
        },
        onConfirm: () => {
          portal.closeDrawer();
          resolve();
        },
      });
    } else {
      resolve();
    }
  });
};

// 未成年人提醒
export const showJuvenilesConfirm: (
  portal: PortalInstance,
  data: { grownUpCount: number; unGrownUpCount: number },
) => Promise<void> = (portal, data) => {
  const { grownUpCount, unGrownUpCount } = data;
  return new Promise((resolve, reject) => {
    if (!portal) reject();
    if (!unGrownUpCount) {
      // 没有未成年人
      resolve();
      return;
    }
    if (!grownUpCount) {
      portal.showDrawer({
        title: intl.get('reservation_cmp_modal_juveniles_reject_title'),
        content: intl.get('reservation_cmp_modal_juveniles_content_all', {
          minors: unGrownUpCount,
        }),
        confirmText: intl.get('app_confirm_verify_btn'),
        onConfirm: () => {
          portal.closeDrawer();
          reject();
        },
      });
    } else {
      portal.showDrawer({
        title: intl.get('reservation_cmp_modal_juveniles_normal_title'),
        content: intl.get('reservation_cmp_modal_juveniles_content_some', {
          adults: grownUpCount,
          minors: unGrownUpCount,
        }),
        cancelText: intl.get('app_confirm_cancel_btn'),
        onCancel: () => {
          portal.closeDrawer();
          reject();
        },
        confirmText: intl.get('app_confirm_verify_btn'),
        onConfirm: () => {
          portal.closeDrawer();
          resolve();
        },
      });
    }
  });
};

// 房卡未退卡
export const showRoomCardConfirm: (portal: PortalInstance, err: ErrRes) => Promise<void> = (
  portal,
  err,
) => {
  return new Promise((resolve) => {
    if (err && err.code === BizErrorCode.ROOM_CARD_NEED_FETCH_BACK) {
      const errMsg = err.msg || '';
      portal.showDrawer({
        title: intl.get('reservation_cmp_modal_room_card_title'),
        content: (
          <div>
            <div>{intl.get('reservation_cmp_modal_room_card_content')}</div>
            <div className={styles.roomText}>{errMsg.split('：')[1] || errMsg}</div>
          </div>
        ),
        confirmText: intl.get('app_confirm_close_btn'),
        className: styles.checkOutConfirmModal,
        onCancel: () => {
          portal.closeDrawer();
          resolve();
        },
        onConfirm: () => {
          portal.closeDrawer();
          resolve();
        },
      });
    } else {
      resolve();
    }
  });
};

// 只有关闭按钮提示
export const showOnlyCloseModal: (
  portal: PortalInstance,
  config: { title: string; content: React.ReactNode },
) => Promise<boolean> = (portal, config) => {
  const { title, content } = config;
  return new Promise((resolve) => {
    portal.showDrawer({
      title,
      content,
      confirmText: intl.get('app_confirm_close_btn'),
      onConfirm: () => {
        portal.closeDrawer();
        resolve(true);
      },
      onCancel: () => {
        portal.closeDrawer();
        resolve(false);
      },
    });
  });
};

// 账单结账提醒
export const showSettlePrompt: (
  portal: PortalInstance,
  orderNo: string, // 主单号
) => Promise<boolean> = (portal, orderNo) => {
  return new Promise((resolve) => {
    if (orderNo) {
      BaseService.getOrderStatus(orderNo).then(
        (res) => {
          const { billStatus, resvStatus } = res.data || {};
          // 账单未结 且 已取消 或者 已退房
          const orderStatusList = [
            MainOrderStatus.CancelReservationOrder,
            MainOrderStatus.CheckoutOrder,
          ];
          if (billStatus === BillStatus.UnSettled && orderStatusList.includes(resvStatus)) {
            portal.showDrawer({
              title: intl.get('reservation_cmp_modal_settle_title'),
              content: intl.get('reservation_cmp_modal_settle_content'),
              cancelText: intl.get('reservation_cmp_modal_settle_cancel_btn'),
              confirmText: intl.get('reservation_cmp_modal_settle_confirm_btn'),
              onCancel: () => {
                resolve(false);
                portal.closeDrawer();
              },
              onConfirm: () => {
                resolve(true);
                portal.closeDrawer();
              },
            });
          } else {
            resolve(false);
          }
        },
        () => {
          // 默认不结账
          resolve(false);
        },
      );
    } else {
      resolve(false);
    }
  });
};

// 某些地区人员提醒
export const showSourceAddressAlert: (
  portal: PortalInstance,
  guestList: CheckInGuestInfo[], // 宾客列表
) => Promise<void> = (portal, guestList) => {
  return new Promise((resolve) => {
    try {
      if (guestList && guestList.length) {
        const matchAddress = '新疆';
        const matchReg = new RegExp(matchAddress);
        const isMatch = guestList.some((item) => {
          if (item.certificateLocation) {
            return item.certificateLocation.match(matchReg);
          }
          return false;
        });
        if (isMatch) {
          portal.showDrawer({
            title: intl.get('reservation_cmp_modal_address_warning_title'),
            content: intl.get('reservation_cmp_modal_address_warning_content', {
              address: matchAddress,
            }),
            confirmText: intl.get('app_confirm_confirm_btn'),
            onConfirm: () => {
              resolve();
              portal.closeDrawer();
            },
            onCancel: () => {
              resolve();
              portal.closeDrawer();
            },
          });
        } else {
          resolve();
        }
      } else {
        resolve();
      }
    } catch (e) {
      resolve();
    }
  });
};

// 退房二次确认
export const showCheckOutPrompt: (portal: PortalInstance) => Promise<boolean> = (portal) => {
  return new Promise((resolve) => {
    portal.showDrawer({
      title: '退房确认',
      content: (
        <div>
          <p className={styles.promptText}>
            {intl.get('reservation_cmp_modal_check_out_confirm_p1')}
          </p>
          <p>{intl.get('reservation_cmp_modal_check_out_confirm_p2')}</p>
          <p>{intl.get('reservation_cmp_modal_check_out_confirm_p3')}</p>
        </div>
      ),
      cancelText: intl.get('app_confirm_cancel_btn'),
      confirmText: intl.get('app_confirm_verify_btn'),
      className: styles.checkOutConfirmModal,
      onCancel: () => {
        resolve(false);
        portal.closeDrawer();
      },
      onConfirm: () => {
        resolve(true);
        portal.closeDrawer();
      },
    });
  });
};
